// ════════════════════════════════════════════════════════════════════════════
// BOT DECK — Nu Gundam Amuro
// Every hook here is called by js/bot_logic/bot-engine.js — see that file's
// header comment for the full contract and why this can't reuse the
// registered player-side card effects in js/effects/*.js directly. "Friendly"
// in this file always means state.oppField/oppHand/oppBase/oppTrash (the
// bot's own side); "enemy" always means state.playerField/hand/base/trash
// (the human). Same conventions as js/bot_logic/decks/marine-tekkadan-aggro.js.
//
// Two small engine extensions were added alongside this deck (not specific to
// it, reusable by any future deck):
//   - resolveShieldHit (game.js): a bot-owned shield with [Burst] text beyond
//     the generic hand/trash placement now redirects to this file's onBurst
//     hook, instead of silently doing nothing (see GD05-105 below).
//   - notifyUnitDestroyedByBattle (game.js): a bot-owned destroyer's paired
//     Pilot now redirects to this file's onLinkedUnitDestroysEnemy hook
//     instead of the real (player-hardcoded) registered handler (see
//     GD05-085 below).
// ════════════════════════════════════════════════════════════════════════════

// ── Shared local helpers ──────────────────────────────────────────────────

function ngaHighestAp(candidates) {
  if (!candidates || candidates.length === 0) return null;
  return candidates.reduce((best, c) => (effectiveBp(c) > effectiveBp(best) ? c : best), candidates[0]);
}

function ngaLondoBellTrashCount() {
  return state.oppTrash.filter(c => (c.trait || '').includes('(Londo Bell)')).length;
}

// Exiles `cards` (real card objects, already found in state.oppTrash) from
// the bot's own trash — mirrors purple.js's exileFromTrash()/
// exileCardsFromTrash() (player-hardcoded: state.playerTrash/playerExile)
// against state.oppTrash/oppExile instead. Used by GD05-017's own [When
// Paired] effect below.
function ngaExileFromOppTrash(cards) {
  cards.forEach(c => {
    const idx = state.oppTrash.findIndex(t => t.uid === c.uid);
    if (idx !== -1) state.oppTrash.splice(idx, 1);
    state.oppTrashCount = Math.max(0, state.oppTrashCount - 1);
    if (state.oppTempDeck) {
      const entry = state.oppTempDeck.find(e => e.cardNo === c.cardNo && e.copyVer === c.copyVer && e.state === 'trash');
      if (entry) entry.state = 'exile';
    }
    state.oppExile.push(c);
  });
}

// Bot-side mirror of peekTopOfDeck(1)/sendDeckEntryToTop/sendDeckEntryToBottom
// (game.js — all hardcoded to state.tempDeck) against state.oppTempDeck
// instead. Used by GD05-029 Kayra's Re-GZ's [Deploy] below.
function ngaPeekTopOfOppDeck() {
  if (!state.oppTempDeck) return null;
  const entry = state.oppTempDeck.filter(isInDeck)[0];
  if (!entry) return null;
  const dbEntry = cardDb[entry.cardNo];
  return dbEntry ? { entry, card: buildGameCardFromDb(dbEntry, entry.copyVer) } : null;
}
function ngaSendOppEntryToTop(entry) {
  const idx = state.oppTempDeck.indexOf(entry);
  if (idx === -1) return;
  state.oppTempDeck.splice(idx, 1);
  entry.state = 'deck';
  state.oppTempDeck.unshift(entry);
}
function ngaSendOppEntryToBottom(entry) {
  const idx = state.oppTempDeck.indexOf(entry);
  if (idx === -1) return;
  state.oppTempDeck.splice(idx, 1);
  entry.state = 'deck';
  state.oppTempDeck.push(entry);
}

// Amuro Ray's (either print, ST01-010/GD05-085) pairing-target priority:
// Gundam (ST01-001, unconditional) > Gundam NT-1 (GD03-001, needs a rested
// 1-HP enemy so its own [When Paired] kill-and-draw actually lands) > Nu
// Gundam GD05-020 (needs the enemy base at 3-or-less HP, or no enemy base at
// all) > Nu Gundam GD05-017 (needs 3+ (Londo Bell) cards in trash, an active
// enemy Unit to target, and must still be able to attack this turn — see its
// own onPair below for why). Shared between both Amuro cards' pairing
// decisions in chooseNextDeployOrActivate and deferFirstAttackPass.
function ngaAmuroPairTarget(ctx) {
  const gundam = ctx.field.find(c => c && c.cardNo === 'ST01-001' && !c.pilot);
  if (gundam) return gundam;

  const nt1 = ctx.field.find(c => c && c.cardNo === 'GD03-001' && !c.pilot);
  if (nt1 && ctx.enemyField.some(c => c && c.rested && effectiveHp(c) === 1)) return nt1;

  const nu1 = ctx.field.find(c => c && c.cardNo === 'GD05-020' && !c.pilot);
  if (nu1) {
    const baseHp = ctx.enemyBase ? effectiveHp(ctx.enemyBase) : 0;
    if (!ctx.enemyBase || baseHp <= 3) return nu1;
  }

  const nu2 = ctx.field.find(c => c && c.cardNo === 'GD05-017' && !c.pilot && !cardCantAttackNow(c));
  if (nu2 && ngaLondoBellTrashCount() >= 3 && ctx.enemyField.some(c => c && !c.rested)) return nu2;

  return null;
}
const NGA_AMURO_PRIORITY_CARDNOS = ['ST01-001', 'GD03-001', 'GD05-020', 'GD05-017'];

registerBotDeck('nu-gundam-amuro', {
  name: 'Nu Gundam Amuro',
  personaName: 'Bot Amuro Ray (CCA)',
  playstyle: 'Cautious / Aggressive',
  decklistSource: '1st Place Gundam Regional Lille',
  representativeCard: 'GD05-017',
  faceIcon: 'assets/bot_face/nu_gundam.webp',
  decklist: {
    'GD01-008': 4, 'GD05-027': 4, 'ST03-008': 4, 'GD05-029': 4, 'GD01-030': 4,
    'ST01-001': 4, 'GD05-019': 4, 'GD03-001': 1, 'GD05-020': 1, 'GD05-017': 4,
    'ST01-010': 4, 'GD05-085': 4, 'GD05-105': 1, 'GD01-100': 2, 'GD05-125': 3,
    'ST02-016': 2,
  },
  sideboard: {},

  // "Default to always attack enemy shield area cards first" — see
  // chooseAttackTarget below. Same "chip away at shields before spending a
  // whole deploy loop" aggressive bias as Marine Aggro.
  attacksBeforeDeploys: true,

  // Defer the pre-deploy attack pass whenever an Amuro Ray pairing is about
  // to happen later this turn — same timing concern (and same fix) as Marine
  // Aggro's Gundam+Amuro combo: every one of this deck's pairings (ST01-001's
  // AP+1-to-all aura, GD03-001/GD05-017's own [When Paired] effects,
  // GD05-020's Breach 3) needs to be live BEFORE the attack pass, not after.
  deferFirstAttackPass(ctx) {
    const amuroInHand = ctx.hand.some(c => c.cardNo === 'ST01-010' || c.cardNo === 'GD05-085');
    if (!amuroInHand) return false;
    if (ngaAmuroPairTarget(ctx)) return true; // a target is already on the field, ready to pair
    // Or the target itself is still in hand too — it'll deploy this turn,
    // then get paired, before the one real attack pass should run.
    return ctx.hand.some(c => NGA_AMURO_PRIORITY_CARDNOS.includes(c.cardNo));
  },

  shouldMulligan(hand) {
    const earlyUnits = hand.filter(c => c.type === 'unit' && (parseInt(c.cost) || 0) <= 2).length;
    return earlyUnits < 2;
  },

  // ── Main Phase driver ──────────────────────────────────────────────────
  chooseNextDeployOrActivate(ctx) {
    const hand = ctx.hand;

    // Tier 0 — Guntank (GD01-008): held in hand until a rested enemy Unit
    // sits at exactly 1 HP — its own [Deploy] can only target a rested
    // enemy, and the deck notes specifically want it saved for a guaranteed
    // kill rather than played on curve.
    const guntank = hand.find(c => c.cardNo === 'GD01-008');
    if (guntank && ctx.canPlayCard(guntank)) {
      const onePointTarget = ctx.enemyField.some(c => c && c.rested && effectiveHp(c) === 1);
      if (onePointTarget) return { kind: 'deploy', uid: guntank.uid };
    }

    // Tier 1 — Amuro Ray pairing (either print; see ngaAmuroPairTarget's own
    // comment for the full priority order and each target's gate).
    // ST01-010 alone gets an extra fallback: pair to ANY unpaired Unit when
    // none of the four priority targets exist in hand OR on the field, and
    // the enemy currently has a Blocker — its own ability (rest an active
    // Blocker with 5 or less HP) is useful regardless of which Unit it
    // rides, per the deck notes. GD05-085 has no such fallback — it simply
    // holds if none of the four targets are available.
    const amuro = hand.find(c => c.cardNo === 'ST01-010' || c.cardNo === 'GD05-085');
    const amuroAffordable = amuro && (ctx.res + ctx.exRes) >= (parseInt(amuro.cost) || 0);
    if (amuroAffordable) {
      const target = ngaAmuroPairTarget(ctx);
      if (target) return { kind: 'pairPilot', pilotUid: amuro.uid, unitUid: target.uid };
      if (amuro.cardNo === 'ST01-010') {
        const priorityAvailable = hand.some(c => NGA_AMURO_PRIORITY_CARDNOS.includes(c.cardNo)) ||
          ctx.field.some(c => c && NGA_AMURO_PRIORITY_CARDNOS.includes(c.cardNo) && !c.pilot);
        const enemyHasBlocker = ctx.enemyField.some(c => c && hasNativeKeyword(c, 'Blocker'));
        if (!priorityAvailable && enemyHasBlocker) {
          const anyUnpaired = ctx.field.find(c => c && !c.pilot);
          if (anyUnpaired) return { kind: 'pairPilot', pilotUid: amuro.uid, unitUid: anyUnpaired.uid };
        }
      }
    }

    // Tier 2 — every other Unit in hand, cheapest first (Guntank excluded —
    // it owns Tier 0's gate above and must never be played through this
    // generic tier when that gate isn't met).
    const otherUnits = hand.filter(c => c.cardNo !== 'GD01-008' && c.type === 'unit' && ctx.canPlayCard(c))
      .sort((a, b) => (parseInt(a.cost) || 0) - (parseInt(b.cost) || 0));
    if (otherUnits.length > 0) return { kind: 'deploy', uid: otherUnits[0].uid };

    // Tier 3 — Exclusively Defense-Oriented Policy (GD05-105): units always
    // come first (Tier 2 above already claims every affordable one each
    // iteration), so reaching here already means none are left to play this
    // turn. Played whenever the enemy has a Lv.3-or-lower Unit to bounce —
    // the handler itself (onActivateCommand/onBurst below) picks Blockers
    // first, then highest AP.
    const edop = hand.find(c => c.cardNo === 'GD05-105');
    if (edop && ctx.canPlayCard(edop)) {
      const hasTarget = ctx.enemyField.some(c => c && (parseInt(c.level) || 0) <= 3);
      if (hasTarget) return { kind: 'activateCommand', uid: edop.uid };
    }

    // Tier 4 — A Show of Resolve (GD01-100): filler, only once nothing else
    // in hand is playable.
    const resolve = hand.find(c => c.cardNo === 'GD01-100');
    if (resolve && ctx.canPlayCard(resolve)) {
      const anythingElsePlayable = hand.some(c => c.uid !== resolve.uid && ctx.canPlayCard(c));
      if (!anythingElsePlayable) return { kind: 'activateCommand', uid: resolve.uid };
    }

    // Tier 5 — Ra Cailum (GD05-125): only while the bot has no base at all.
    const raCailum = hand.find(c => c.cardNo === 'GD05-125');
    if (raCailum && ctx.canPlayCard(raCailum) && !ctx.base) {
      return { kind: 'deployBase', uid: raCailum.uid };
    }

    // Tier 5b — Ra Cailum's [Activate･Main], once it's already the base:
    // "Rest this Base" is its entire cost (no RES, no [Once per Turn] tag),
    // so the rested base itself is the only gate needed against repeated
    // activation this same turn.
    if (ctx.base?.cardNo === 'GD05-125' && !ctx.base.rested) {
      const hasLondoBellTarget = ctx.field.some(c => c && (c.trait || '').includes('(Londo Bell)'));
      if (hasLondoBellTarget) return { kind: 'activateBase' };
    }

    // Tier 6 — Corsica Base (ST02-016): only while the bot has no base, at
    // Lv.3+, and 2 or more Unit cards in hand currently can't be played
    // (resources better spent on a base than sitting idle behind unplayable
    // units).
    const corsica = hand.find(c => c.cardNo === 'ST02-016');
    if (corsica && ctx.canPlayCard(corsica) && !ctx.base && ctx.level >= 3) {
      const unplayableUnits = hand.filter(c => c.type === 'unit' && c.uid !== corsica.uid && !ctx.canPlayCard(c)).length;
      if (unplayableUnits >= 2) return { kind: 'deployBase', uid: corsica.uid };
    }

    return null;
  },

  // ── [Deploy] effects ───────────────────────────────────────────────────
  onDeploy: {
    // Guntank: "Choose 1 rested enemy Unit. Deal 1 damage to it." Tier 0's
    // gate already guarantees a 1-HP rested candidate exists; highest-AP
    // tiebreak if there happen to be more than one.
    'GD01-008': (card) => {
      const candidates = state.playerField.filter(c => c && c.rested && effectiveHp(c) === 1);
      if (candidates.length === 0) { addLog(`${card.name} — no rested enemy Units with 1 HP. No effect.`); return; }
      const target = ngaHighestAp(candidates);
      dealDamageToUnit(target, 1, { fromEnemy: true });
      addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
      render(); setupDropZones();
    },

    // Kayra's Re-GZ: "Look at the top card of your deck. Return it to the
    // top or bottom of your deck." No deck-notes guidance for this specific
    // choice — keeps it on top when it's something playable at the bot's
    // current level (so the next draw is useful), otherwise bottoms it.
    'GD05-029': (card) => {
      const peek = ngaPeekTopOfOppDeck();
      if (!peek) { addLog(`${card.name} — deck is empty.`); return; }
      const { entry, card: topCard } = peek;
      const keepOnTop = (parseInt(topCard.cost) || 0) <= (state.oppLevel || 0);
      if (keepOnTop) {
        ngaSendOppEntryToTop(entry);
        addLog(`${card.name} — looked at top card (${topCard.name}): kept it on top.`);
      } else {
        ngaSendOppEntryToBottom(entry);
        addLog(`${card.name} — looked at top card (${topCard.name}): sent it to the bottom.`);
      }
    },

    // Nu Gundam (GD05-020): "If there are 2 or more (Londo Bell) cards in
    // your trash, place 1 EX Resource." Mirrors placeExResource(false)
    // (game.js, hardcoded to state.exRes/playerField) against oppExRes.
    'GD05-020': (card) => {
      if (ngaLondoBellTrashCount() < 2) return;
      state.oppExRes = (state.oppExRes || 0) + 1;
      BotEngine.recomputeOppLevel();
      addLog(`${card.name} — [Deploy] 2+ (Londo Bell) cards in trash: placed 1 EX Resource.`);
      broadcastCounters(); render();
    },
  },

  // ── [Destroyed] effects (see game.js's triggerCardEffect for why this
  // can't just be the real registered handler — js/effects/green.js's own
  // GD05-019 handler explicitly skips unless ctx.ownerSide === 'player') ──
  onDestroyed: {
    // Re-GZ: "Look at the top 3 cards of your deck. You may reveal 1 (Londo
    // Bell) Unit card among them and add it to your hand. Return the
    // remaining cards randomly to the bottom of your deck." Deck notes:
    // "Always trigger its effect when this unit gets destroyed by any
    // means" — no further choice described, so the first qualifying card
    // found is always taken when one exists.
    'GD05-019': (card) => {
      if (!state.oppTempDeck) return;
      const peeked = state.oppTempDeck.filter(isInDeck).slice(0, 3);
      if (peeked.length === 0) { addLog(`${card.name} — deck is empty. No effect.`); return; }
      const match = peeked.find(entry => {
        const db = cardDb[entry.cardNo];
        return db && db.categoryData === 'unit' && (db.trait || '').includes('(Londo Bell)');
      });
      const remaining = peeked.filter(e => e !== match);
      if (match) {
        match.state = 'hand';
        const db = cardDb[match.cardNo];
        const handCard = buildGameCardFromDb(db, match.copyVer);
        state.oppHand.push(handCard);
        state.oppHandCount = state.oppHand.length;
        addLog(`${card.name} — revealed ${handCard.name} and added it to hand.`);
      }
      for (let i = remaining.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
      }
      remaining.forEach(entry => ngaSendOppEntryToBottom(entry));
      state.oppDeckCount = state.oppTempDeck.filter(isInDeck).length;
      render(); setupDropZones();
    },
  },

  // ── Base [Deploy] effects (beyond the generic "add 1 Shield to hand",
  // already handled by bot-engine.js's applyOppBaseDeployEffect) ─────────
  onDeployBase: {
    // Corsica Base: deploys 1 Tallgeese token, or 2 Leo tokens instead if a
    // card named "Corsica Base" is already in trash — but only on the bot's
    // own turn (the card text says "if it is your turn"; this hook also
    // fires when Corsica Base bursts from the bot's shield zone during the
    // HUMAN's turn, where no token should deploy at all).
    'ST02-016': (card) => {
      if (state.myTurn) return; // not the bot's own turn
      const hasCorsicaInTrash = state.oppTrash.some(c => (c.name || '').includes('Corsica Base'));
      if (hasCorsicaInTrash) {
        BotEngine.deployTokenToOppField('T-004');
        BotEngine.deployTokenToOppField('T-004');
        addLog(`${card.name} — deployed 2 Leo tokens.`);
      } else {
        const token = BotEngine.deployTokenToOppField('T-005');
        if (token) addLog(`${card.name} — deployed Tallgeese token.`);
      }
      render(); setupDropZones();
    },
  },

  // ── Base [Activate･Main] effects ───────────────────────────────────────
  onActivateBase: {
    // Ra Cailum: "Choose 1 friendly (Londo Bell) Unit. During this turn,
    // when it receives enemy damage, reduce it by 1." Deck notes: "target
    // friendly units that will be attacking enemy units" — prefer an
    // attack-eligible (Londo Bell) Unit; fall back to any if none can
    // currently attack.
    'GD05-125': (base) => {
      const candidates = state.oppField.filter(c => c && (c.trait || '').includes('(Londo Bell)'));
      if (candidates.length === 0) { addLog(`${base.name} — no friendly (Londo Bell) Units. No effect.`); return; }
      const attackEligible = candidates.filter(c => !cardCantAttackNow(c));
      const target = (attackEligible.length ? attackEligible : candidates)[0];
      target.tempIncomingDamageReduction = (target.tempIncomingDamageReduction || 0) + 1;
      addLog(`${base.name} — ${target.name} will reduce enemy damage by 1 this turn.`);
      render(); setupDropZones();
    },
  },

  // ── Command [Main]/[Action]/[Burst] effects ────────────────────────────
  onActivateCommand: {
    'GD01-100': (card) => {
      const drawn = BotEngine.botDrawCards(2);
      state.oppHand.push(...drawn);
      state.oppHandCount = state.oppHand.length;
      addLog(`${card.name} — drew ${drawn.length} card(s).`);
      render(); setupDropZones();
    },

    // Exclusively Defense-Oriented Policy: "Choose 1 enemy Unit that is
    // Lv.3 or lower. Return it to its owner's hand." Deck notes: Blockers
    // first, then highest AP. Shared with onBurst below — "[Burst] Activate
    // this card's [Main]" is the exact same effect.
    'GD05-105': (card) => {
      const candidates = state.playerField.filter(c => c && (parseInt(c.level) || 0) <= 3);
      if (candidates.length === 0) { addLog(`${card.name} — no enemy Units Lv.3 or lower. No effect.`); return; }
      const blockers = candidates.filter(c => hasNativeKeyword(c, 'Blocker'));
      const target = ngaHighestAp(blockers.length ? blockers : candidates);
      BotEngine.botBouncePlayerUnitToHand(target);
      addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
    },
  },

  // Bot-owned shield with [Burst] text beyond generic hand/trash placement
  // (see resolveShieldHit, game.js) — "[Burst]Activate this card's [Main]."
  onBurst: {
    'GD05-105': (card) => {
      window.BotDecks['nu-gundam-amuro'].onActivateCommand['GD05-105'](card);
    },
  },

  // ── [When Paired] effects (fires on whichever cardNo owns the text —
  // the pilot's, the unit's, or both) ────────────────────────────────────
  onPair: {
    // Amuro Ray (ST01-010): "Choose 1 active enemy Unit with 5 or less HP.
    // Rest it." Deck notes: always a Blocker first — highest HP, then
    // highest AP as the tiebreak either way.
    'ST01-010': (pilot) => {
      const candidates = state.playerField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
      if (candidates.length === 0) { addLog(`${pilot.name} — no active enemy Units with 5 or less HP.`); return; }
      const blockers = candidates.filter(c => hasNativeKeyword(c, 'Blocker'));
      const pool = blockers.length ? blockers : candidates;
      let target = pool[0];
      pool.forEach(c => {
        if (effectiveHp(c) > effectiveHp(target) ||
            (effectiveHp(c) === effectiveHp(target) && effectiveBp(c) > effectiveBp(target))) {
          target = c;
        }
      });
      setRested(target, true);
      addLog(`${pilot.name} — ${target.name} rested.`);
      render(); setupDropZones();
    },

    // Gundam NT-1's own [When Paired]: "Choose 1 rested enemy Unit. Deal 1
    // damage to it. When this effect destroys an enemy Unit, draw 1." The
    // pairing gate (ngaAmuroPairTarget) already guarantees a rested 1-HP
    // enemy exists, so this damage is always lethal — highest-AP tiebreak
    // per the deck notes ("pick the enemy unit with the highest AP and
    // 1 HP").
    'GD03-001': (unit) => {
      const candidates = state.playerField.filter(c => c && c.rested && effectiveHp(c) === 1);
      if (candidates.length === 0) { addLog(`${unit.name} — no rested enemy Units with 1 HP.`); return; }
      const target = ngaHighestAp(candidates);
      const targetUid = target.uid;
      dealDamageToUnit(target, 1, { fromEnemy: true });
      if (!state.playerField.find(c => c && c.uid === targetUid)) {
        const drawn = BotEngine.botDrawCards(1);
        if (drawn.length > 0) {
          state.oppHand.push(drawn[0]);
          state.oppHandCount = state.oppHand.length;
          addLog(`${unit.name} — destroyed enemy Unit, drew a card.`);
        }
      }
      render(); setupDropZones();
    },

    // Nu Gundam GD05-017's own [When Paired]: "You may choose 3 (Londo
    // Bell) cards from your trash. Exile them from the game. If you do,
    // choose 1 enemy Unit. Begin a battle between this Unit and it and only
    // perform the damage step." The pairing gate already guarantees 3+
    // (Londo Bell) trash cards and an active enemy exist — reuses the exact
    // same dispatch('ATTACK', {skipToDamage:true, ...}) call the real
    // player-side handler uses (js/effects/green.js), just with mine:false
    // (same side-agnostic convention bot-engine.js's own real attacks use).
    // Target: the active enemy Unit with the LEAST AP, per the deck notes.
    'GD05-017': (unit) => {
      const londoBellCards = state.oppTrash.filter(c => (c.trait || '').includes('(Londo Bell)'));
      if (londoBellCards.length < 3) { addLog(`${unit.name} — fewer than 3 (Londo Bell) cards in trash. No effect.`); return; }
      const toExile = londoBellCards.slice(0, 3);
      ngaExileFromOppTrash(toExile);
      addLog(`${unit.name} — exiled ${toExile.map(c => c.name).join(', ')}.`);
      const activeEnemies = state.playerField.filter(c => c && !c.rested);
      broadcastCounters(); render(); setupDropZones();
      if (activeEnemies.length === 0) { addLog(`${unit.name} — no active enemy Units to target. No further effect.`); return; }
      const target = activeEnemies.reduce((lowest, c) => (effectiveBp(c) < effectiveBp(lowest) ? c : lowest), activeEnemies[0]);
      dispatch('ATTACK', {
        attackerUid: unit.uid,
        defenderUid: target.uid,
        isShield: false,
        isDirect: false,
        skipToDamage: true,
        mine: false,
      });
    },
  },

  // Standing (not turn-scoped) passive on a paired Pilot — see
  // notifyUnitDestroyedByBattle's bot redirect (game.js). Amuro Ray
  // (GD05-085): "During your turn, when this Unit destroys an enemy Unit
  // with battle damage, this Unit recovers 2 HP." deferredApplyHpMod (not
  // applyHpMod) — same ordering reason as the real handler: this fires
  // before the Unit's own counter-damage from the same exchange is applied,
  // which would otherwise cap the recovery to 0 on an undamaged Unit.
  onLinkedUnitDestroysEnemy: {
    'GD05-085': (pilot, unit, destroyedUnit) => {
      if (state.myTurn) return; // must be the bot's own turn
      deferredApplyHpMod([unit], 2);
      addLog(`${pilot.name} — ${unit.name} destroyed ${destroyedUnit.name} in battle. Recovers 2 HP.`);
    },
  },

  // ── Attack phase ────────────────────────────────────────────────────────
  // "Default to always attack enemy shield area cards first with units with
  // highest AP if there is a enemy base or lowest to highest AP if there
  // are no enemy base" — applied uniformly as the attack ORDER (no
  // card-specific ordering was requested beyond this), while
  // chooseAttackTarget below handles each card's own target preference.
  chooseAttackOrder(ctx) {
    const hasEnemyBase = !!ctx.enemyBase;
    const ordered = [...ctx.attackers].sort((a, b) =>
      hasEnemyBase ? effectiveBp(b) - effectiveBp(a) : effectiveBp(a) - effectiveBp(b));
    return ordered.map(c => c.uid);
  },

  chooseAttackTarget(attacker, validTargets, ctx) {
    const shieldTarget = validTargets.find(t => t.isShield);
    const baseTarget   = validTargets.find(t => t.type === 'base');
    const directTarget = validTargets.find(t => t.isDirect);
    const fallback = () => shieldTarget || baseTarget || directTarget || validTargets[0];
    const toValidTarget = (unit) => unit && validTargets.find(t => t.uid === unit.uid);

    switch (attacker.cardNo) {
      // Guntank: shields first, UNLESS the enemy has 2+ Blockers (a Blocker
      // would just redirect the hit anyway) — then prefer base/direct/any
      // Unit over wasting the attack on a shield attempt.
      case 'GD01-008': {
        const blockerCount = ctx.enemyField.filter(c => c && hasNativeKeyword(c, 'Blocker')).length;
        if (blockerCount >= 2) return baseTarget || directTarget || validTargets.find(t => !t.isShield) || validTargets[0];
        return shieldTarget || fallback();
      }

      // Jegan, Zaku II, Kayra's Re-GZ: always the shield area.
      case 'GD05-027':
      case 'ST03-008':
      case 'GD05-029': {
        if (attacker.cardNo === 'ST03-008') {
          // [Attack] This Unit gets AP+2 during this turn — the real
          // registered handler (js/effects/green.js) only fires for
          // attackerSide==='player', so it never reaches a bot-owned
          // attacker (see bot-engine.js's header on fireAttacker*Trigger's
          // same-side gate). Applied here instead, right before the attack
          // declares, matching where the real trigger would have run.
          attacker.tempApMod = (attacker.tempApMod || 0) + 2;
        }
        return shieldTarget || fallback();
      }

      // Rick Dom: a rested enemy Unit where (enemy AP < Rick Dom's current
      // HP) OR (enemy HP <= Rick Dom's current AP) — highest-AP tiebreak.
      case 'GD01-030': {
        const myHp = effectiveHp(attacker), myAp = effectiveBp(attacker);
        const candidates = ctx.enemyField.filter(c => c && c.rested && (effectiveBp(c) < myHp || effectiveHp(c) <= myAp));
        return toValidTarget(ngaHighestAp(candidates)) || fallback();
      }

      // Gundam (ST01-001): a rested enemy Unit with AP less than Gundam's
      // current HP — highest-AP tiebreak.
      case 'ST01-001': {
        const myHp = effectiveHp(attacker);
        const candidates = ctx.enemyField.filter(c => c && c.rested && effectiveBp(c) < myHp);
        return toValidTarget(ngaHighestAp(candidates)) || fallback();
      }

      // Re-GZ: shield area first; otherwise a rested enemy Unit with HP
      // equal to or less than Re-GZ's own current AP — highest-AP tiebreak.
      case 'GD05-019': {
        if (shieldTarget) return shieldTarget;
        const myAp = effectiveBp(attacker);
        const candidates = ctx.enemyField.filter(c => c && c.rested && effectiveHp(c) <= myAp);
        return toValidTarget(ngaHighestAp(candidates)) || fallback();
      }

      // Gundam NT-1 (GD03-001): a rested enemy Unit with AP less than
      // NT-1's current HP — highest-AP tiebreak.
      case 'GD03-001': {
        const myHp = effectiveHp(attacker);
        const candidates = ctx.enemyField.filter(c => c && c.rested && effectiveBp(c) < myHp);
        return toValidTarget(ngaHighestAp(candidates)) || fallback();
      }

      // Nu Gundam (GD05-020): once paired, any enemy Unit with AP less than
      // its current HP — highest-AP tiebreak; unpaired, shield area.
      case 'GD05-020': {
        if (!attacker.pilot) return shieldTarget || fallback();
        const myHp = effectiveHp(attacker);
        const candidates = ctx.enemyField.filter(c => c && effectiveBp(c) < myHp);
        return toValidTarget(ngaHighestAp(candidates)) || fallback();
      }

      // Nu Gundam (GD05-017): paired — a rested enemy Unit with AP less
      // than its current HP; unpaired — any enemy Unit with AP less than
      // its current HP (not restricted to rested). Highest-AP tiebreak
      // either way.
      case 'GD05-017': {
        const myHp = effectiveHp(attacker);
        const candidates = attacker.pilot
          ? ctx.enemyField.filter(c => c && c.rested && effectiveBp(c) < myHp)
          : ctx.enemyField.filter(c => c && effectiveBp(c) < myHp);
        return toValidTarget(ngaHighestAp(candidates)) || fallback();
      }

      default:
        return fallback();
    }
  },

  // ── Defense (human attacks the bot) ────────────────────────────────────
  // No card in this deck has <Blocker> (Guntank, Jegan, Zaku II, Kayra's
  // Re-GZ, Rick Dom, Gundam, Re-GZ, NT-1, Nu Gundam x2 are all blocker-less),
  // so decideBlock's own eligible-Blocker filter (bot-engine.js) never
  // reaches these hooks in practice — included anyway for contract
  // completeness, matching Marine Aggro's convention.
  shouldBlock() {
    return true;
  },
  chooseBlockTarget(willing) {
    let best = willing[0];
    willing.forEach(c => { if (effectiveHp(c) < effectiveHp(best)) best = c; });
    return best.uid;
  },
});
